<script>
    import { Row, Col, Button, Input, Alert } from 'sveltestrap'
    import { createEventDispatcher } from 'svelte'
    import Icon from 'svelte-awesome'
    import { plus } from 'svelte-awesome/icons'
    import FormAlerts from '../FormAlerts.svelte'
    import FormSavingButtons from '../FormSavingButtons.svelte'
    import AffiliationInfo from './AffiliationInfo.svelte'

    export let context
    export let saving
    
    let acknowledged = false
    let formDisabled
    let typesExhausted = false
    let buttonText = 'Add an Affiliation'
    
    const fanXLightDefaultAffiliations = [
        { key: "current_student", value: 'Current Student' },
        { key: "season_ticket_holder", value: 'Coach' },
        { key: "premium_seat_member", value: 'Parent' },
        { key: "faculty_and_staff", value: 'Faculty & Staff' },
        { key: "fan", value: 'Fan' },
    ]
    
    $: {
        buttonText = addButtonDisabled ? 'Max affiliations reached' : 'Add an Affiliation'
    }

    $: addButtonDisabled = context.view.affiliations.length >= 7

    const dispatch = createEventDispatcher()
    $: {
        // if they are adding affiliations for the first time
        // we need to enable them in the portal and set the default order

        const hasAffiliations = !!Object.keys(context.form.affiliations.attributes.affiliations).find(key => context.form.affiliations.attributes.affiliations[key] !== ''
        )

        context.form.school.attributes.featuresEnabledInApp.affiliations = hasAffiliations

        if (!hasAffiliations) {
            context.form.affiliations.attributes.order = Object.keys(context.view.affiliationSchema)
        }

        dispatch('change', {
            formKey: 'school',
            keypath: 'attributes.featuresEnabledInApp.affiliations',
            value: hasAffiliations,
            context
        })

        if(context.view.affiliations.length === 0) {
            context.view.affiliations = [...fanXLightDefaultAffiliations]
            const converted = fanXLightDefaultAffiliations.reduce((obj, item) => ({
                ...obj,
                [item.key]: item.value
            }), {})
            context.form.affiliations.attributes.affiliations = {...converted}
        }
    }

    $: {
        formDisabled = context.view.affiliations.length === 0 && !acknowledged && !typesExhausted
    }

    const addAffiliation = () => {
        // determine what key can be used
        let assignedAffiliationType;

        Object.keys(context.view.affiliationSchema).forEach(schemaAffiliation => {
            // do not auto-assign reward_users affiliation type
            if (schemaAffiliation === 'reward_users') return

            const usedKeys = context.view.affiliations.map(({ key }) => key)

            if (usedKeys.includes(schemaAffiliation)) return;

            assignedAffiliationType = schemaAffiliation
        })

        if (!assignedAffiliationType) typesExhausted = true

        context.view.affiliations = [
            ...context.view.affiliations,
            {
                key: assignedAffiliationType,
                value: ''
            }
        ]
    }
</script>

<h1>
    Affiliations
</h1>
<p>
    Affiliations can be thought of as segments for content and messaging. All new users of the
    app will be asked to choose how they are affiliated via the list of labels you create.
    Notifications, Promotions, Fan Guide Items, or Special Events can all utilize affiliations
    to direct the user experience within the app.
</p>

<h2>Add Your Affiliations</h2>
<p>
    You can create a maximum of 7 affiliations for your app, though you are not required to use
    the full amount. The Affiliation labels you create are fully customizable to represent your
    brand. See the example list below for ideas.
</p>

<p>
    To start, click <em>Add an Affiliation +</em> below to begin creating your labels.
</p>

    <Row class="my-3">
        <Col xs="8">
            {#each context.view.affiliations as affiliation, index (affiliation.key)}
                <AffiliationInfo
                    {index}
                    bind:context
                    formKey="affiliations"
                    {affiliation}
                    affiliationSchema={context.view.affiliationSchema}
                    on:updateContext
                />
            {/each}

            <div>
                <Button on:click={addAffiliation} disabled={addButtonDisabled}>
                    {buttonText}
                    {#if !addButtonDisabled}
                        <Icon data={plus} />
                    {/if}
                </Button>
            </div>
        </Col>
        <Col xs="4">
            <p>Examples</p>
            <ul>
                <li>Alumni</li>
                <li>Current Student</li>
                <li>Parent</li>
                <li>Faculty & Staff</li>
                <li>Fan</li>
                <li>Premium Seat Member</li>
                <li>Season Ticket Holder</li>
                <li>Visitor</li>
                <li>Booster Club Member</li>
                <li>Student-Athlete</li>
                <li>Student-Athlete Parent</li>
            </ul>
        </Col>
    </Row>

{#if context.view.affiliations.length === 0}
    <Row>
        <Col xs="6">
            <Alert color='primary'>
                <Input type="checkbox" bind:checked={ acknowledged } class="fnoex-list-item-selection me-2" />
                I acknowledge that I do not want affiliations
            </Alert>
        </Col>
    </Row>
{/if}

<FormAlerts bind:alerts={context.alerts} />

<FormSavingButtons
    {context}
    {saving}
    disableSave={formDisabled}
    disableNext={formDisabled}
    on:save
    on:nextStepClicked
    on:previousStepClicked />
    